import { IScreenAnchorPositionType } from '@r3f-component-data-structure';
import { ITransitionPhase } from '@r3f/transitions/spec';
import { useFrame } from '@react-three/fiber';
import React, { useRef } from 'react';
import { Group, OrthographicCamera } from 'three';
import { Jute } from '..';
import { useGetSubscribedSceneTransitionPhaseRef } from '../components/hoc/hocCustomHooks';
import { IOverflowEdge, IOverflowHit, measureAnchorGroupOverflow } from '../utils/screen-relative-overflow';
import { useDataStore } from '../zustand';

/**
 * How often to measure. Polling rather than reacting to specific events on purpose: one trigger covers a
 * viewport resize, `applyContentUpdate`, async glTF/texture/troika-text layout, scene navigation and
 * animation resets, with no subscriptions to keep in step. Troika in particular has no synchronous moment
 * at which its text geometry bounds are final.
 */
const SAMPLE_INTERVAL_S = 0.25;

/**
 * Consecutive samples that must agree before a change is published.
 *
 * Absorbs asset pop-in, where an entity's bounds are briefly wrong while a texture or model resolves.
 * Note this is a "same answer twice" rule, not a "wait until nothing is moving" one — a looped animation
 * never stops moving, so the latter would never publish at all.
 */
const STABLE_SAMPLES = 2;

interface IInput {
	sceneId: string;
	isActiveScene: boolean;
	cameraRef: React.RefObject<OrthographicCamera>;
	groupsRef: React.RefObject<Map<IScreenAnchorPositionType, Group>>;
	/** Entity ids per anchor group, from the content doc. */
	entityIdsByAnchor: Partial<Record<IScreenAnchorPositionType, string[]>>;
}

const signatureOf = (ids: string[]): string => ids.slice().sort().join(',');

/**
 * Watch this scene's screen-relative content for anything the current viewport cuts off, and report it
 * to the host through `Jute.screenContentOverflowChanged`.
 *
 * Preview-simulator only. Content anchored to `center` keeps a fixed offset from the middle of the
 * screen, so anything parked near an edge there falls off a narrower viewport than the one it was
 * authored on — which the author cannot see until they open the experience somewhere else. An edge or
 * corner anchor group moves with the frustum, so it is immune on the sides it is pinned to but not on the
 * opposite ones — see `ANCHORED_EDGES` in `utils/screen-relative-overflow.ts`.
 */
export const useScreenRelativeOverflow = ({ sceneId, isActiveScene, cameraRef, groupsRef, entityIdsByAnchor }: IInput): void => {
	const componentsById = useDataStore((state) => state.componentsById);
	const transitionPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);

	const elapsedRef = useRef(0);
	const flaggedRef = useRef<Set<string>>(new Set());
	const pendingRef = useRef({ signature: '', count: 0 });
	const publishedRef = useRef<string | null>(null);
	const hitsRef = useRef<IOverflowHit[]>([]);

	useFrame((_state, delta) => {
		// Registered unconditionally — a hook cannot be — so the gate is here. One boolean per frame is
		// what this costs a published experience.
		if (!Jute.isPreviewSimulator) return;

		const camera = cameraRef.current;
		const groups = groupsRef.current;
		if (!camera || !groups || !componentsById) return;

		if (!isActiveScene) {
			// Say "all clear" once for a scene that is no longer showing, then go quiet. Without this the
			// host would keep displaying a warning about a scene the user has navigated away from.
			if (publishedRef.current !== null && publishedRef.current !== '') {
				flaggedRef.current.clear();
				pendingRef.current = { signature: '', count: 0 };
				publishedRef.current = '';
				Jute.screenContentOverflowChanged({ sceneId, entityIds: [], entityTypes: [], anchors: [], edges: [] });
			}
			return;
		}

		elapsedRef.current += delta;
		if (elapsedRef.current < SAMPLE_INTERVAL_S) return;
		elapsedRef.current = 0;

		// Belt-and-braces with measuring the authored transform: this stops us reading a scene whose
		// entities are still mounting.
		if (transitionPhaseRef.current !== ITransitionPhase.enterFinish) return;

		const hits = hitsRef.current;
		hits.length = 0;
		for (const anchor of Object.keys(entityIdsByAnchor) as IScreenAnchorPositionType[]) {
			const group = groups.get(anchor);
			const entityIds = entityIdsByAnchor[anchor];
			if (!group || !entityIds?.length) continue;
			measureAnchorGroupOverflow({ group, anchor, entityIds, componentsById, camera, flagged: flaggedRef.current, out: hits });
		}

		const entityIds = hits.map((hit) => hit.entityId);
		const entityTypes = entityIds.map((id) => componentsById[id].type);
		const signature = signatureOf(entityIds);

		// Require the same answer twice before telling the host, so a half-loaded asset does not flash a
		// warning on and off.
		const pending = pendingRef.current;
		if (signature === pending.signature) {
			pending.count += 1;
		} else {
			pending.signature = signature;
			pending.count = 1;
		}
		if (pending.count < STABLE_SAMPLES || signature === publishedRef.current) return;

		flaggedRef.current = new Set(entityIds);
		publishedRef.current = signature;

		const anchors: IScreenAnchorPositionType[] = [];
		const edges: IOverflowEdge[] = [];
		for (const hit of hits) {
			if (!anchors.includes(hit.anchor)) anchors.push(hit.anchor);
			for (const edge of hit.edges) if (!edges.includes(edge)) edges.push(edge);
		}

		Jute.screenContentOverflowChanged({ sceneId, entityIds, entityTypes, anchors, edges });
	});
};
